import { Fragment, forwardRef } from "react";
import { Droppable, Draggable } from "@hello-pangea/dnd";
import { Eye, GripVertical, Trash2 } from "lucide-react";
import SectionRenderer from "@/components/SectionRenderer";

/**
 * Right-pane live preview for the Blog Editor. Mirrors the published
 * BlogReader article: header (title/subtitle/byline/dates/header image),
 * mini TOC on the left, drag-reorderable article body, and the author bio card.
 *
 * The parent owns `previewRefs` (map of section-id → DOM node) so it can
 * scroll to a specific section when a TOC row is clicked; we thread that ref
 * map through and mirror the DnD `ref` back into it.
 */
const BlogEditorPreview = forwardRef(function BlogEditorPreview(
  {
    blog, authors, editingSectionId, veSettings, previewRefs,
    InsertDivider, openSection, confirmDelete, removeSectionWithToast, scrollPreviewTo,
    order,
  },
  ref,
) {
  return (
    <div ref={ref} data-testid="preview-pane" className="overflow-y-auto bg-white" style={{ order }}>
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-200 px-6 py-2.5 flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
        <Eye className="w-3 h-3"/> Live preview
        <span className="text-slate-400 normal-case tracking-normal">— double-click a section to edit</span>
      </div>

      <div className="px-6 md:px-8 py-8">
        <div className="grid grid-cols-[160px_minmax(0,1fr)] gap-6">
          {/* Mini TOC — mirrors BlogReader desktop TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-14">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Blog Contents</div>
              {(() => {
                // Mirror the public BlogReader TOC: exclude hidden sections,
                // sections without a heading level, and untitled sections
                // (heading empty or whitespace-only).
                const tocSections = blog.sections.filter(
                  s => !s.hidden && s.level > 0 && (s.heading || "").trim(),
                );
                return (
                  <nav className="space-y-1 border-l border-slate-200">
                    {tocSections.map(s => (
                      <button
                        key={s.id}
                        data-testid={`preview-toc-${s.id}`}
                        onClick={() => scrollPreviewTo(s.id)}
                        onDoubleClick={() => openSection(s.id)}
                        className={`block w-full text-left pl-3 py-1 text-[13px] transition-colors ${
                          editingSectionId === s.id
                            ? "border-l-2 -ml-px border-sky-700 text-sky-800 font-semibold"
                            : "text-slate-500 hover:text-slate-900"
                        } ${s.level === 1 ? "toc-h1 text-[14px]" : s.level === 3 ? "toc-h3 text-[12px]" : "toc-h2"}`}
                      >
                        {s.heading}
                      </button>
                    ))}
                    {tocSections.length === 0 && (
                      <div className="pl-3 py-1 text-xs italic text-slate-400">No titled sections</div>
                    )}
                  </nav>
                );
              })()}
            </div>
          </aside>

          {/* Article body */}
          <article className="max-w-3xl">
            <header className="mb-8 pb-8 border-b border-slate-200">
              <h1 className="font-heading text-4xl md:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.1]">
                {blog.title || "Untitled"}
              </h1>
              {blog.subtitle && (
                <p className="mt-3 font-heading text-xl md:text-2xl text-slate-600 dark:text-slate-400 font-normal italic leading-snug">
                  {blog.subtitle}
                </p>
              )}
              {(blog.author_name || blog.first_published || blog.last_updated) && (
                <div className="mt-4 text-sm text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1">
                  {blog.author_name && <span>By <span className="text-slate-700 font-medium">{blog.author_name}</span></span>}
                  {blog.first_published && <span>Published {new Date(blog.first_published).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>}
                  {blog.last_updated && blog.last_updated !== blog.first_published && <span>· Updated {new Date(blog.last_updated).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</span>}
                </div>
              )}
              {blog.header_image_url && (
                <figure className="mt-6 rounded-md overflow-hidden border border-slate-200">
                  <img src={blog.header_image_url} alt={blog.header_image_alt || blog.title}
                       className="w-full h-auto block"/>
                </figure>
              )}
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <button disabled
                        className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-slate-300 rounded-md text-slate-500 opacity-70 cursor-not-allowed">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3-7 3V5z"/>
                  </svg>
                  Bookmark
                </button>
                <span className="text-xs text-slate-500">
                  Added {new Date(blog.created_at || Date.now()).toLocaleDateString()}
                </span>
                {blog.release_date && blog.release_date > new Date().toISOString().slice(0,10) && (
                  <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">
                    Scheduled · {blog.release_date}
                  </span>
                )}
              </div>
            </header>

            <Droppable droppableId="preview-sections">
              {(prov) => (
                <div ref={prov.innerRef} {...prov.droppableProps} className="blog-content">
                  {blog.sections.map((s, i) => (
                    <Fragment key={s.id || `p-${i}`}>
                      {i === 0 && <InsertDivider index={0} variant="preview"/>}
                      <Draggable draggableId={`p-${String(s.id || i)}`} index={i}>
                        {(dp, snap) => (
                          <section ref={(el) => { dp.innerRef(el); previewRefs.current[s.id] = el; }}
                                   {...dp.draggableProps}
                                   data-testid={`preview-section-${s.id}`}
                                   onDoubleClick={() => openSection(s.id)}
                                   title={s.hidden ? "Hidden from the published blog — double-click to edit" : "Double-click to edit this section"}
                                   className={`group relative transition-all ${s.hidden ? "opacity-40" : ""} ${
                                     snap.isDragging ? "bg-sky-50 ring-2 ring-sky-300 rounded-md shadow-lg"
                                       : editingSectionId === s.id ? "ring-2 ring-sky-400 bg-sky-50/40 rounded-md" : ""}`}>
                            <div className={`absolute -left-8 top-2 flex flex-col gap-0.5 transition-opacity ${snap.isDragging ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                                 onClick={e => e.stopPropagation()}>
                              <div {...dp.dragHandleProps}
                                   className="w-6 h-6 grid place-items-center rounded text-slate-300 hover:text-slate-700 hover:bg-slate-100 cursor-grab active:cursor-grabbing"
                                   aria-label="Drag section">
                                <GripVertical className="w-4 h-4"/>
                              </div>
                              <button
                                onClick={() => confirmDelete(
                                  "Delete section?",
                                  `"${s.heading || "Untitled section"}" will be removed. You can undo with Ctrl+Z.`,
                                  () => removeSectionWithToast(s.id, s.heading),
                                )}
                                className="w-6 h-6 grid place-items-center rounded text-slate-300 hover:text-red-600 hover:bg-red-50"
                                aria-label="Delete section" title="Delete section">
                                <Trash2 className="w-4 h-4"/>
                              </button>
                            </div>
                            <SectionRenderer section={s} veSettings={veSettings}/>
                          </section>
                        )}
                      </Draggable>
                      <InsertDivider index={i + 1} variant="preview"/>
                    </Fragment>
                  ))}
                  {prov.placeholder}
                  {blog.sections.length === 0 && (
                    <p className="text-slate-400 italic">No sections yet. Click "Add section" on the left to start.</p>
                  )}
                </div>
              )}
            </Droppable>

            {(() => {
              const a = authors.find(x => x.author_id === blog.author_id);
              if (!a) return null;
              return (
                <div className="mt-16 pt-8 border-t border-slate-200">
                  <div className="text-xs font-bold uppercase tracking-[0.24em] text-slate-500 mb-3">Author</div>
                  <div className="font-heading text-xl font-semibold text-slate-900">{a.name}</div>
                  {a.title && <div className="text-sm text-slate-500 mt-0.5">{a.title}</div>}
                  {a.bio && <p className="mt-2 text-slate-600 leading-relaxed max-w-2xl">{a.bio}</p>}
                </div>
              );
            })()}
          </article>
        </div>
      </div>
    </div>
  );
});

export default BlogEditorPreview;
